import { Box, Container, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Stack, Switch, TablePagination, } from '@mui/material';
import { ArrowDropUp, ArrowDropDown } from '@mui/icons-material';
import { useEffect, useState } from 'react';
import SettingsModal from './modals/settings/settingsModal';
import DeleteSettingsModal from './modals/settings/deleteSettingsModal';
import { toast } from 'react-toastify';
import { sortItems } from '../common/utils/tableUtil';
import PageHeader from '../common/banner/pageHeader';
import ClassificationBannerHeader from '../common/banner/classifactionBannerHeader';
import SearchBar from '../common/search/SearchBar';

const ProgramTable = ({ items, onToggle, sortConfig, onSortChange, setPrograms, fetchPrograms }) => {

  const handleUpdate = (category) =>  {
    toast.success(`${category} Updated`, { autoClose: 3000 });
    fetchPrograms();
  }

  const handleDelete = (setting) =>  {
    setPrograms(items.filter(items => items.id !== setting.id));
    toast.success(`${setting.category} Deleted`, { autoClose: 3000 });
  }

  const handleSort = (key) => {
    const direction =
      sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc';
    onSortChange({ key, direction });
  }

  const SortIcon = ({ field }) => {
    if (sortConfig.key !== field) return null;
    return sortConfig.direction === 'asc' ? (
      <ArrowDropUp fontSize="small" />
    ) : (
      <ArrowDropDown fontSize="small" />
    )
  }

  return (
    <>
      <TableContainer component={Paper} sx={{ mt: 4 }}>
        <Table sx={{ minWidth: 650 }} aria-label="simple table">    
          <TableHead>
            <TableRow sx={{ backgroundColor: '#0568AE' }}>
              {['category', 'parameter', 'setting', 'human_edit', 'phen_edit'].map((field) => (
                <TableCell
                  key={field}
                  onClick={() => handleSort(field)}
                  sx={{ color: '#EEEEEE', fontWeight: 'bold', cursor: 'pointer', userSelect: 'none' }}
                >
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <span>{field.charAt(0).toUpperCase() + field.slice(1)}</span>
                    <SortIcon field={field} />
                  </Stack>
                </TableCell>
              ))}
              <TableCell sx={{ color: '#EEEEEE', fontWeight: 'bold' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item, index) => (
              <TableRow key={item.id} sx={{ height: 56, backgroundColor: index % 2 === 0 ? '#2A2A2A' : '#000000' }}>
                <TableCell sx={{ color: '#FFFFFF' }}>{item.category}</TableCell>
                <TableCell sx={{ color: '#FFFFFF' }}>{item.parameter}</TableCell>
                <TableCell sx={{ color: '#FFFFFF' }}>{item.setting}</TableCell>
                <TableCell>
                  <Switch checked={!!item.human_edit} onChange={(e) => onToggle(item, 'human_edit', e.target.checked)} />
                </TableCell>
                <TableCell>
                  <Switch checked={!!item.phen_edit} onChange={(e) => onToggle(item, 'phen_edit', e.target.checked)} />
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={1}>
                    <SettingsModal setting={ item } onTriggerUpdate={handleUpdate} />
                    <DeleteSettingsModal setting={ item } onTriggerDelete={handleDelete} />
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

export default function ProgramSettings() {
  const [programs, setPrograms] = useState([]);  
  const [sortConfig, setSortConfig] = useState({ key: 'id', direction: 'asc' });
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    fetchPrograms();
  }, []);

  const fetchPrograms = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API}/programs/settings`);
      if (!res.ok) throw new Error('Failed to fetch');
      const response = await res.json();
      setPrograms(response.data);
    } catch (e) {
      console.error(e);
    }
  };

  const handleCreate = (category) =>  {
    toast.success(`${category} Created `, { autoClose: 3000 });
    fetchPrograms();
  }
  
  const handleUpdate = async (item) => {

      try {
          const res = await fetch(`${process.env.REACT_APP_API}/programs/settings/${item.id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(item)
          });
          if (res.ok) {
            const programId = item.id;
            const updatedProgram = programs.find(program => program.id === programId);

            setPrograms((prev) => prev.map((p) => (p.id === item.id ? item : p)));

            if (( item.human_edit ) && ( updatedProgram.human_edit !== item.human_edit )) {
                toast.success(`${item.category} Can Be Edited By Humans`);
            } else if (( !item.human_edit ) && ( updatedProgram.human_edit !== item.human_edit )) {
                toast.success(`${item.category} Can No Longer Be Edited By Humans`);
            } else if (( item.phen_edit ) && ( updatedProgram.phen_edit !== item.phen_edit )) {
                toast.success(`${item.category} Can Be Edited By Phen`);
            } else if (( !item.phen_edit ) && ( updatedProgram.phen_edit !== item.phen_edit )) {
                toast.success(`${item.category} Can No Longer Be Edited By Phen`);
            }
          } else {
            toast.error(`Error Updating Setting : ${res.error}`);
        }
      } catch(err) {
        toast.error("Error Updating Setting : ", err);
      }
  };

  const handleSortChange = (newConfig) => {
    setSortConfig(newConfig);
  };

  const filtered = programs.filter((item) => {
    const s = searchQuery.toLowerCase();
    return ['category', 'parameter', 'setting'].some((field) => item[field]?.toLowerCase().includes(s));
  });

  const sorted = sortItems(filtered, sortConfig);
  const paginated = sorted.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Container sx={{ minWidth:1800, mt: 10, mb: 10 }}>    
        <Box sx={{pt: 2}}>
            <ClassificationBannerHeader></ClassificationBannerHeader>
            <PageHeader title="Settings"></PageHeader>
        </Box>

        <Box>
          <Stack direction="row" justifyContent="flex-end" mt={2} mb={1} sx={{ width: '100%', mx: 'auto' }}>
            <SearchBar
                value={searchQuery}
                onChange={setSearchQuery}
                placeholder="Search Settings..."
                label="Search Settings..."
                width={200}
            />                
          </Stack>

          <TableContainer component={Paper} sx={{ mt: 4, backgroundColor: '#1A1A1A'  }}>
            <ProgramTable
              items={paginated}          
              onToggle={(item, key, value) => handleUpdate({ ...item, [key]: value })}
              sortConfig={sortConfig}
              onSortChange={handleSortChange}
              setPrograms={setPrograms}
              fetchPrograms={fetchPrograms}
            />
            <TablePagination
              component="div"
              count={sorted.length}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => { setRowsPerPage(+e.target.value); setPage(0); }}
              rowsPerPageOptions={[5, 10, 25, 50]}
              sx={{ color: 'white', '& .MuiSvgIcon-root': { color: 'white' } }}
            />
          </TableContainer>

          <Stack direction="row" mt={2} mb={1}>
            <SettingsModal setting='' onTriggerCreate={handleCreate} />
          </Stack>
        </Box>	
    </Container>
  );
}
